import React, { useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core';
import axios from 'axios';
import clsx from 'clsx';
import cookie from 'cookie_js';
import * as Raven from 'raven-js';
import { useForm } from 'react-hook-form';
import { hashHistory } from 'react-router';

import PhoneCallButton from '../../../../src/pages/LoginConfirm/components/PhoneCallButton';
import { Track } from '../../../weborder/analytics';
import * as Api from '../../../weborder/Api';
import { cookieConsent } from '../../../weborder/cookieconsent';
import { Utilities } from '../../../weborder/Utilities';
import { Button, IconButton, TextField, Typography } from '../../atoms';
import { ArrowLeft as ArrowLeftIcon } from '../../icons';
import { Dialog } from '../../molecules';

const useStyles = makeStyles((theme: Theme) => ({
  loginConfirmDialog: {
    '& button': {
      display: 'block',
    },

    '& .MuiTypography-h1': {
      marginBottom: theme.spacing(0.5),
    },

    '& .MuiTypography-body1': {
      color: theme.palette.grey[800],
      marginBottom: theme.spacing(1),
    },

    '& .MuiTypography-body2': {
      color: theme.palette.grey[800],
      marginBottom: theme.spacing(2),

      [theme.breakpoints.up('sm')]: {
        fontSize: theme.typography.fontSize,
      },
    },

    '& .MuiFormControl-root': {
      marginBottom: theme.spacing(2.5),
    },
  },

  mb3: {
    marginBottom: theme.spacing(3.125),
  },
}));

interface FormInput {
  smsCode: string;
}
interface CodePayload {
  deviceId: string;
  PhoneNumber: string;
  rememberMe?: boolean;
  selectedDeviceDeliveryLocationId: number | undefined;
  SmsCode: string;
}

interface Props {
  configData: Api.AppConfig;
  afterLoginUrl: string;
  allowGuestLogin: boolean;
  deviceId: string;
  enableRememberMe: boolean;
  hasGdprCookieFeatureFlag: boolean;
  isFunctionalCookieConsentEnabled: boolean;
  login: () => void;
  params: any;
  rememberMe: boolean;
  setRememberMe: (boolean) => void;
  translate: any;
}

export const LoginConfirmDialog = (props: Props): JSX.Element => {
  const {
    afterLoginUrl,
    allowGuestLogin,
    configData,
    deviceId,
    enableRememberMe,
    hasGdprCookieFeatureFlag,
    isFunctionalCookieConsentEnabled,
    login,
    params,
    rememberMe,
    setRememberMe,
    translate,
  } = props;

  const classes = useStyles();

  const [callRequested, setCallRequested] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [processingCode, setProcessingCode] = useState(false);
  const [smsCode, setSmsCode] = useState('');

  const { handleSubmit, register, reset } = useForm<FormInput>();

  const smsCodeAriaLabel = `${translate('Your_code_was_sent_to')} ${
    params.phone
  }`;

  const setAuthCookie = (flipdishToken: string): any => {
    if (!flipdishToken) return;

    const date = new Date();
    date.setMonth(date.getMonth() + 6); // Expires in 6 months

    const shouldPreserveAuthCookie =
      (hasGdprCookieFeatureFlag &&
        (isFunctionalCookieConsentEnabled || rememberMe)) ||
      !hasGdprCookieFeatureFlag;

    cookie.set('flipdish-token', flipdishToken, {
      expires: shouldPreserveAuthCookie ? date : undefined,
    });
  };

  const applyFlipdishToken = (flipdishToken) => {
    setAuthCookie(flipdishToken);

    if (flipdishToken) {
      axios.defaults.headers.common['X-Flipdish-Token'] = flipdishToken;
    }
  };

  const onSubmit = (data: FormInput) => {
    setProcessingCode(true);
    setErrorMessage('');

    let payload: CodePayload = {
      deviceId,
      PhoneNumber: params.phone,
      selectedDeviceDeliveryLocationId: params.dlid,
      SmsCode: data.smsCode,
    };

    if (hasGdprCookieFeatureFlag && enableRememberMe) {
      payload = { ...payload, rememberMe };
    }

    axios
      .post('/Account/LoginUsingPhoneNumber/', payload)
      .then((response) => {
        applyFlipdishToken(response.headers['x-flipdish-token']);

        if (enableRememberMe && rememberMe) {
          // Enable functional cookies when remember me checked!
          const selected = cookieConsent.getConsentCookies();
          const hasFunctionalCookie =
            selected && selected.some((cookie) => cookie === 'functional');

          if (!hasFunctionalCookie) {
            selected.push('functional');
            cookieConsent.setConsentCookies(selected);
          }
          setRememberMe(false);
        }

        const result: Api.Result<any> = response.data;

        if (result.Success) {
          login();
          hashHistory.replace(afterLoginUrl);
          Track('Login', 'SMS Code Entry Successful');
        } else {
          setProcessingCode(false);
          setErrorMessage(
            translate('LoginConfirm_LoginConfirmDialog_InvalidLogin'),
          );
          reset();

          Track('Login', 'SMS Code Entry Failed');
        }
      })
      .catch((error) => {
        alert(translate('There_was_an_error_logging_in'));

        const dataLog: any = {};

        dataLog.AppNameId = configData.AppNameId;
        dataLog.WhiteLabelId = configData.WhiteLabelId;

        Raven.captureMessage('Error with SMS code submission', {
          tags: { category: 'API failure' },
          extra: {
            error: Utilities.handleError(error),
            appconfig: dataLog,
            phone: params.phone,
            SmsCode: data.smsCode,
          },
        });
        Track('Login', 'SMS Code Entry Failed');
      });

    Track('Login', 'Submit SMS Code Button');
  };

  const handleSmsNumberChange = (event: any) => {
    if (event.target.value.length >= 4) {
      handleSubmit(onSubmit)();
    }
  };

  const handleBackLink = () => {
    const backlinkPath = params.dlid ? `/phone/${params.dlid}` : '/phone';

    hashHistory.replace(backlinkPath);
    Track('Login', 'Change Phone');
  };

  const handleGuestLogin = () => {
    axios
      .post(`/Account/guestUserLogin?wlid=${configData.AppNameId}`)
      .then((response) => {
        applyFlipdishToken(response.headers['x-flipdish-token']);

        const result: Api.Result<any> = response.data;

        if (result.Success) {
          login();
          hashHistory.replace(afterLoginUrl);

          Track('Login', 'Guest Login Successful');
        } else {
          setProcessingCode(false);
          setSmsCode('');
          setErrorMessage(translate('There_was_an_error_logging_in'));

          Track('Login', 'Guest Login Failed');
        }
      })
      .catch((error) => {
        alert(props.translate('There_was_an_error_logging_in'));

        const appconfig: any = {};
        const dataLog: any = {};

        appconfig.AppNameId = configData.AppNameId;
        appconfig.WhiteLabelId = configData.WhiteLabelId;

        Raven.captureMessage('Error with Guest login process', {
          tags: { category: 'API failure' },
          extra: {
            error: Utilities.handleError(error),
            appconfig: dataLog,
            phone: params.phone,
            SmsCode: smsCode,
          },
        });
        Track('Login', 'Guest Login Failed');
      });

    Track('Login', 'Submit Guest Login');
  };

  const handlePhonecallError = () => {
    setErrorMessage(translate('There_was_an_error_requesting_a_phone_call'));
    Track('PhoneCall', 'Fallback Phone Call for SMS Code Failed');
  };

  const handlePhonecallOnSubmit = () => {
    setCallRequested(true);
    setErrorMessage('');
  };

  const handlePhonecallSuccess = () => {
    Track('PhoneCall', 'Fallback Phone Call for SMS Code Successful');
  };

  const callRequestStyle = clsx(callRequested && classes.mb3);
  const callRequestMessage = callRequested
    ? translate('you_will_receive_call_soon')
    : translate('LoginConfirm_LoginConfirmDialog_DidntGetCode');

  return (
    <Dialog
      className={classes.loginConfirmDialog}
      open={true}
      startIcon={
        <IconButton
          aria-label={translate('Back')}
          className="iconLeft"
          color="secondary"
          data-fd="back-link"
          onClick={handleBackLink}
        >
          <ArrowLeftIcon />
        </IconButton>
      }
    >
      <Typography variant="h1">{translate('Enter_four_digit_code')}</Typography>

      <Typography variant="body2">
        {translate('Your_code_was_sent_to')} {params.phone}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          aria-label={smsCodeAriaLabel}
          autoFocus={true}
          data-fd="sms-input"
          defaultValue=""
          fullWidth
          helperText={errorMessage}
          id="smsCode"
          inputRef={register}
          key="smsCode"
          name="smsCode"
          onChange={handleSmsNumberChange}
          status={errorMessage ? 'error' : 'default'}
          type="number"
        />

        <Typography className={callRequestStyle}>
          {callRequestMessage}
        </Typography>

        {allowGuestLogin && (
          <Button
            className={classes.mb3}
            data-fd="guest-login"
            onClick={handleGuestLogin}
            variant="text"
          >
            {translate('login_sms_guestCheckout')}
          </Button>
        )}

        <PhoneCallButton
          onError={() => handlePhonecallError()}
          onSubmit={handlePhonecallOnSubmit}
          onSuccess={handlePhonecallSuccess}
          PhoneNumber={params.phone}
          translate={translate}
        >
          {({ button }) => (
            <Button
              {...button}
              data-fd="request-call"
              disabled={button.disabled || processingCode}
              size="small"
              variant="secondary"
            />
          )}
        </PhoneCallButton>
      </form>
    </Dialog>
  );
};
